import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  fract, sin, cos, mix, smoothstep, clamp, max, abs,
  positionLocal, positionPrevious, positionWorld, uv,
  normalize, dot, negate, shadow
} from 'three/tsl';
import { CONFIG } from '../../config';
import { fbm2, gradNoise2, cell2, vnoise2, domainWarp2 } from './tsl/noise';

type FloatNode = ReturnType<typeof float>;

// Lava surround for the Boxing Ring map — a layered, physically-motivated
// surface that reads the way real seawater / hero-shot lava shaders do:
//
//   A. Deep flow     — slow IQ-warped fbm, sets base heat and the flow field.
//   B. Mid swells    — 3-octave fbm riding the deep-flow warp vector.
//   C. Capillary     — fast gradient-noise ripples; analytic derivatives
//                      drive a fake-sun shading term so the crests shimmer.
//   D. Crust plates  — cellular (Worley) pattern; cooled-rock in plate
//                      interiors + thin glowing seams where plates meet.
//   E. Hot-spots     — sparse slow blob field for bubbling upwellings.
//   F. Fake-sun      — diffuse term from the height-field normal.
//   G. 5-stop ramp   — blackbody-ish crust → mantle → mid → hot → white-hot.
//
// All of this runs in one fragment pass on a 1-segment plane — no geometry
// tessellation, no additional render targets, and the bloom threshold in
// PostPipeline (0.85) picks up the white-hot tips + plate seams for glow.
//
// Positions are sampled in world XZ (`positionWorld.xz`) so the pattern is
// anchored to world space — the lava doesn't crawl when the camera moves.
export class LavaMaterial extends NodeMaterial {
  // ── Public / legacy uniforms (preserved for external tuning / Tweakpane).
  readonly uTime        = uniform(0);
  // The hot core crosses 1.0 on the red channel so the bloom pass catches
  // it; the mid/crust colours stay sub-unit so the molten field reads as
  // material rather than lightsource.
  readonly uHot         = uniform(new THREE.Vector3(1.15, 0.52, 0.10));
  readonly uMid         = uniform(new THREE.Vector3(0.50, 0.12, 0.02));
  readonly uCrust       = uniform(new THREE.Vector3(0.06, 0.025, 0.02));
  readonly uFlowScale   = uniform(CONFIG.render.lava.flowScale);
  // uFlowSpeed kept for API stability; deep flow now uses uDeepSpeed below.
  readonly uFlowSpeed   = uniform(CONFIG.render.lava.deepSpeed);
  readonly uEmissiveMul = uniform(CONFIG.render.lava.emissiveMul);

  // ── New layered uniforms. Defaults are the tuned visual baseline.
  readonly uDeepSpeed     = uniform(CONFIG.render.lava.deepSpeed);
  readonly uSwellScale    = uniform(0.55);
  readonly uSwellSpeed    = uniform(CONFIG.render.lava.swellSpeed);
  readonly uRippleScale   = uniform(2.2);
  readonly uRippleSpeed   = uniform(CONFIG.render.lava.rippleSpeed);
  readonly uRippleAmp     = uniform(CONFIG.render.lava.rippleAmp);
  readonly uPlateScale    = uniform(0.35);
  readonly uPlateSeamGlow = uniform(1.4);
  readonly uHotSpotScale  = uniform(0.08);
  readonly uHotSpotSpeed  = uniform(0.12);
  readonly uShadeStrength = uniform(CONFIG.render.lava.shadeStrength);
  readonly uHeightGain    = uniform(7.0);
  // Geometric-wave knobs. uWaveAmp scales the displacement magnitude *and*
  // the normal contribution in lock-step so the shading stays matched to
  // the geometry even when the user drops amplitude to near-zero.
  // uWaveSpeed scales phase velocity (slowing the whole wave field without
  // changing the wavelengths/directions). uWaveShadeGain is a pure visual
  // emphasis on the shading side — the physical wave slopes are gentle
  // (~3.8° peak) so without the gain the waves would barely shade.
  readonly uWaveAmp       = uniform(CONFIG.render.lava.waveAmp);
  readonly uWaveSpeed     = uniform(CONFIG.render.lava.waveSpeed);
  readonly uWaveShadeGain = uniform(CONFIG.render.lava.waveShadeGain);
  // Ring keep-out mask. Distance to ring = max(|x|,|z|) (axis-aligned
  // square, matching the ring geometry). Displacement fades to zero
  // between `maskStart` and `maskEnd` so the surface stays flat right
  // under the boxing ring — critical now that waveAmp=2.5 makes peak
  // crests tall enough to otherwise poke through the ring base.
  readonly uWaveRingMaskStart = uniform(CONFIG.render.lava.waveRingMaskStart);
  readonly uWaveRingMaskEnd   = uniform(CONFIG.render.lava.waveRingMaskEnd);
  // Bright near-white-hot tip of the ramp. Red and green both cross 1.0 so
  // the white-hot peaks bloom strongly — the literal hot-spots and the
  // interiors of plate seams.
  readonly uCoreWhite     = uniform(new THREE.Vector3(1.6, 1.2, 0.7));
  // Sun direction (pointing *toward* the light source from the surface).
  // Matches the scene's key DirectionalLight at (-3.5, 6, 3) normalised
  // (Renderer.ts:48) so the fake shading is consistent with the rest of
  // the scene's lighting direction.
  readonly uSunDir        = uniform(new THREE.Vector3(-0.47, 0.80, 0.40));
  // ±1 flip for the height-field normal's XZ components. If the wrong
  // side of the waves is getting highlighted after a visual check, flip
  // this via CONFIG.render.lava.normalFlip — cheap to toggle, per the
  // project's verify-directions discipline.
  readonly uNormalSign    = uniform(CONFIG.render.lava.normalFlip ? -1 : 1);

  constructor() {
    super();
    // Self-emissive only — ignore dir light / env IBL. Otherwise the PBR
    // integrator would multiply our procedural colour by a cos-lambert
    // term and the lava would read as "tinted rock" instead of "glowing".
    this.lights = false;

    // ── Sum-of-sines heightfield. Shared between the vertex path
    // (displaces geometry upward) and the fragment path (provides
    // analytic normals for shading). Both sample at the same world-XZ
    // so the geometry and shading stay perfectly aligned.
    //
    // Three detuned waves with hand-picked directions/wavelengths. Lava
    // is viscous so the phase velocities are slow (0.33–0.50 m/s) and
    // the amplitudes are modest — peak height sum of (0.30+0.17+0.08) =
    // 0.55 m, clearing the ~0.95 m gap under the ring skirt at y=-3.2.
    //
    // Directions are pre-normalised (|dir|=1) so `k = 2π/λ` reads as the
    // wavenumber in world-meters directly.
    //
    // Returns vec3(height, dH/dx, dH/dz) in world units, pre-uWaveAmp.
    const lavaWaves = Fn((args: [Node<'vec2'>, Node<'float'>]) => {
      const [p, t] = args;

      // Wave parameters. Each column: (dir.x, dir.y, wavelength, amplitude, speed).
      const dirs = [
        { x:  0.9578, y:  0.2873, lambda: 28, A: 0.30, speed: 0.42 },
        { x: -0.5145, y:  0.8575, lambda: 16, A: 0.17, speed: 0.33 },
        { x:  0.5812, y: -0.8137, lambda:  9, A: 0.08, speed: 0.50 }
      ];

      let height: Node<'float'> = float(0);
      let gx:     Node<'float'> = float(0);
      let gz:     Node<'float'> = float(0);
      for (const w of dirs) {
        const k = (2 * Math.PI) / w.lambda;
        const omega = k * w.speed;
        const d = vec2(w.x, w.y);
        const phase = p.dot(d).mul(k).sub(t.mul(omega));
        const s = sin(phase);
        const c = cos(phase);
        // H   += A sin(k·d·p - ω t)
        // dHx += A k dx cos(...)
        // dHz += A k dy cos(...)
        height = height.add(s.mul(w.A));
        gx = gx.add(c.mul(w.A * k * w.x));
        gz = gz.add(c.mul(w.A * k * w.y));
      }
      return vec3(height, gx, gz);
    });

    // Distance-to-ring mask, axis-aligned square using max(|x|,|z|) so it
    // matches the ring's footprint geometry exactly. Returns 0 inside the
    // ring keep-out zone, 1 outside the fade band, smoothstep between.
    // Shared between positionNode (zero displacement near ring) and
    // colorNode (zero wave-normal contribution where there's no wave, so
    // shading tracks geometry).
    const ringMask = Fn((args: [Node<'vec2'>]) => {
      const [worldXZ] = args;
      const d = max(abs(worldXZ.x), abs(worldXZ.y));
      return smoothstep(this.uWaveRingMaskStart, this.uWaveRingMaskEnd, d);
    });

    // Vertex displacement. The lava plane is authored in its local XY and
    // the mesh is rotated x=-π/2 so local +Z maps to world +Y. Lifting a
    // vertex upward in world therefore means *adding* to its local Z.
    //
    // World-XZ for sampling: after the -π/2 rotation around X, local +Y
    // maps to world -Z. So worldXZ = (localX, -localY). Sampling at this
    // coordinate means the wave field is anchored to world space, not to
    // the plane's local frame — and the fragment shader's positionWorld.xz
    // (which equals the same value since vertical displacement doesn't
    // change XZ) stays perfectly aligned with what we computed here.
    // `positionPrevious.assign(displaced)` ties the velocity MRT's
    // "previous" vertex position to the current displaced one, so the
    // wave displacement doesn't show up as spurious per-pixel motion.
    // Without this, three's default VelocityNode compares the displaced
    // `positionLocal` against the undisplaced `positionGeometry` and
    // reports the static component of our displacement as a permanent
    // smear. The time-varying component (frame-to-frame wave delta) is
    // sub-pixel at our amplitude/speed, so losing it from the velocity
    // signal is imperceptible — matrix-driven motion (camera/transform)
    // still blurs correctly.
    this.positionNode = Fn(() => {
      const worldXZ = vec2(positionLocal.x, negate(positionLocal.y));
      const wave = lavaWaves(worldXZ, this.uTime.mul(this.uWaveSpeed));
      const mask = ringMask(worldXZ);
      const h = wave.x.mul(this.uWaveAmp).mul(mask);
      const displaced = positionLocal.add(vec3(0, 0, h));
      positionPrevious.assign(displaced);
      return displaced;
    })();

    this.colorNode = Fn(() => {
      const p0 = positionWorld.xz.toVar();
      const t = this.uTime;
      const baseScale = this.uFlowScale;

      // ── Layer A: deep flow. IQ 2-layer domain warp gives both the base
      // heat *and* the flow vector q2 that later layers ride on top of.
      const deepT = t.mul(this.uDeepSpeed);
      const deep = domainWarp2(p0, deepT, baseScale); // vec3(heatA, q2x, q2y)
      const heatA = deep.x;
      const q2 = vec2(deep.y, deep.z);

      // ── Layer B: mid swells. Three octaves is enough — this layer
      // contributes body/volume to the heat field, not sharp detail, so
      // more octaves would be wasted. Offsetting by q2 makes it *drift
      // with* the deep flow rather than translating independently, which
      // is the thing that makes layered flows read as one fluid.
      const swellP = p0
        .mul(baseScale.mul(this.uSwellScale))
        .add(q2.mul(0.4))
        .add(vec2(t.mul(this.uSwellSpeed), 0));
      const heatB = fbm2(swellP, 3);

      // ── Layer C: capillary ripples with analytic gradient. `gradNoise2`
      // returns vec3(value, dF/dPx, dF/dPy); we use the value for heat and
      // the derivatives (chained through the scale) for the normal.
      //
      // Anisotropic time offset (x-speed = 0.7× y-speed) gently stretches
      // the ripples along one direction, breaking the "isotropic seething"
      // look and giving the surface a suggested flow direction.
      const rippleScale = baseScale.mul(this.uRippleScale);
      const rippleP = p0
        .mul(rippleScale)
        .add(q2.mul(0.15))
        .add(vec2(t.mul(this.uRippleSpeed).mul(0.7), t.mul(this.uRippleSpeed)));
      const grad = gradNoise2(rippleP);
      const heatC = grad.x;
      // Chain rule: derivatives in world-space are derivatives in
      // ripple-space scaled by dRippleP/dWorld = rippleScale.
      const dHdx = grad.y.mul(rippleScale).mul(this.uHeightGain);
      const dHdz = grad.z.mul(rippleScale).mul(this.uHeightGain);

      // ── Layer D: cooled-crust plates. cell2 returns vec2(F1, F2-F1):
      //   plateInterior = F1   (0 at plate centre, ~0.7+ at edges)
      //   seamDist      = F2-F1 (small at seams between plates, wider in
      //                          the interior). We invert the seam via
      //                          smoothstep(0.03, 0, seamDist) so the
      //                          thin glowing line sits right on cell
      //                          boundaries.
      // Warping the plate coords by q2*0.8 makes plates slowly drift and
      // deform instead of sitting rigidly on the world grid.
      const plateP = p0.mul(baseScale.mul(this.uPlateScale)).add(q2.mul(0.8));
      const cell = cell2(plateP);
      const plateInterior = cell.x.toVar();
      const seamDist = cell.y;

      // ── Layer E: traveling hot-spots. Sparse thresholded low-frequency
      // noise that marks regions of the surface as currently bubbling. The
      // threshold+smoothstep keeps only the top fraction of the blob field
      // so most of the surface is unaffected and the hot-spots punch
      // through the bloom threshold visibly.
      const hotSpotP = p0.mul(this.uHotSpotScale).add(vec2(t.mul(this.uHotSpotSpeed), 0));
      const hotSpot = smoothstep(0.55, 0.78, vnoise2(hotSpotP));

      // ── Combined heat. Weights tuned so the base flow still dominates
      // (0.65 authority of its 0..1 range after the multiplier) but each
      // layer contributes visibly; plateInterior *cools* the field
      // slightly in cell centres, biasing the colour ramp toward crust
      // for the cooled-rock plate look.
      const heat = clamp(
        heatA
          .add(heatB.mul(0.35))
          .add(heatC.mul(this.uRippleAmp))
          .add(hotSpot.mul(0.25))
          .sub(plateInterior.mul(0.08)),
        0,
        1
      ).toVar();

      // ── Layer G: 5-stop chromatic temperature ramp. Each `mix` blends
      // the previous-step colour toward the next stop over a smoothstep
      // range on heat. Rough mantle colour is built from `uMid * 0.35` so
      // it auto-follows the mid tuning without another uniform.
      const crustTinted = mix(this.uCrust.mul(0.6), this.uCrust, plateInterior);
      const c1 = crustTinted;
      const c2 = mix(c1, this.uMid.mul(0.35), smoothstep(0.15, 0.40, heat));
      const c3 = mix(c2, this.uMid,           smoothstep(0.40, 0.62, heat));
      const c4 = mix(c3, this.uHot,           smoothstep(0.62, 0.82, heat));
      const c5 = mix(c4, this.uCoreWhite,     smoothstep(0.82, 0.98, heat));

      // ── Layer F: fake-sun diffuse shading. Two normal contributions:
      //   1) Geometric waves (same lavaWaves function the vertex path
      //      uses for displacement) — analytic derivatives in world
      //      units. Multiplied by uWaveShadeGain to emphasise what
      //      would otherwise be subtle (~3.8° peak slopes on slow lava
      //      swells), and by uWaveAmp so dropping wave amplitude drops
      //      wave shading in lock-step with the geometry.
      //   2) Capillary ripples — the fake-height gradient layer C
      //      produces. Provides the high-frequency shimmer on top of
      //      the slow wave structure.
      // Adding their XZ derivatives before building the normal lets the
      // two tilt contributions compose naturally through `normalize`.
      const waveShade = lavaWaves(p0, t.mul(this.uWaveSpeed));
      // Apply the same ring mask used in positionNode so the shading
      // contribution of the waves drops to zero exactly where the
      // geometry goes flat. Without this, the shader would light
      // phantom waves on the static patch under the ring.
      const waveMask = ringMask(p0);
      const waveGain = this.uWaveAmp.mul(this.uWaveShadeGain).mul(waveMask);
      const totalDHdx = waveShade.y.mul(waveGain).add(dHdx);
      const totalDHdz = waveShade.z.mul(waveGain).add(dHdz);

      const nSign = this.uNormalSign;
      const n = normalize(vec3(
        negate(totalDHdx).mul(nSign),
        float(1),
        negate(totalDHdz).mul(nSign)
      ));
      const sunDir = normalize(this.uSunDir);
      const ndl = max(dot(n, sunDir), 0);
      // Never let the shading go below 0.7 — lava should *glow* even in
      // shadow, the sun term is an extra brightening on crests, not a
      // diffuse-lit-material multiplier.
      const shade = float(0.7).add(ndl.mul(this.uShadeStrength));

      // ── Plate-seam emissive line. Thin (smoothstep edge 0.03) so it
      // reads as a crack of molten rock between cooled plates rather than
      // a wide glowing border.
      const seam = smoothstep(0.03, 0.0, seamDist);
      const seamGlow = this.uHot.mul(seam).mul(this.uPlateSeamGlow);

      // ── Global compose. Subtle throb on the whole sheet so the surface
      // breathes independently of the local motion layers.
      const body = c5.mul(shade).add(seamGlow);
      const throb = sin(t.mul(0.6)).mul(0.05).add(1.0);

      return vec4(body.mul(this.uEmissiveMul).mul(throb), 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
  setEmissiveMul(m: number): void { this.uEmissiveMul.value = m; }
}

// Shared braid pattern. Returns a 0..1 shading factor keyed off the cylinder's
// circumferential uv (uv.x around, uv.y along). Three diagonal strands spiral
// around the axis at `pitch` full rotations per unit of axial length (the
// axial length in metres is already baked into the UV range via `length`, so
// pitch reads as "twists per unit of uv.y" = "twists per length").
//
// `cos((s - 0.5) * 2π) * 0.5 + 0.5` peaks at 1.0 at the strand centre-line
// and drops to 0.0 at the seam between strands. It's smoother than a triangle
// wave, which reads as a more convincing rounded strand cross-section.
const braidShading = Fn((args: [Node<'vec2'>, Node<'float'>, Node<'float'>]) => {
  const [u, length, pitch] = args;
  // s ∈ [0, 1], three repeats around (3 strands), sliding along axis at pitch.
  const s = fract(u.x.mul(3).add(u.y.mul(length).mul(pitch)));
  return sin(s.mul(Math.PI));
});

// Vertex-node wobble for boxing-ring ropes + braided strand albedo.
//
// * positionNode: centred tube (positionLocal.x ∈ [-L/2, +L/2]); a pair of
//   detuned sin waves give a gentle ambient sway that sits on top of the
//   rigid-body translation the renderer applies each frame. An end-mask
//   clamps wobble to zero at both post attachment points.
// * colorNode: 3-strand braid via `braidShading` modulates a base purple-
//   indigo albedo. `lights = true` is inherited, so Three's lighting model
//   shades the braided albedo naturally using the cylinder's radial normals.
//
// `phase` offsets each rope in time so the 12 ropes in a ring don't sway in
// lockstep — the feel is "ropes tied to posts after someone just bumped them"
// rather than a synchronised wave machine.
export class RopeMaterial extends NodeMaterial {
  readonly uTime    = uniform(0);
  readonly uLength  = uniform(1);
  readonly uPhase   = uniform(0);
  // Ambient wobble amplitude. Sits on top of the rigid-body translation the
  // renderer applies each frame, so there's always a soft sway even when no
  // one's touching the rope. 3.5 cm reads as "flappy" without straying so
  // far that it looks unattached from the posts.
  readonly uAmp     = uniform(0.035);
  // Purple-indigo rope. Cool tone sits cleanly against the warm lava below
  // instead of blending into it the way the original red did.
  readonly uColor   = uniform(new THREE.Vector3(0.353, 0.247, 0.682));
  // Braid pitch — full rotations of the strand triplet per metre of rope.
  // At rope radius ~4 cm, a pitch of 4 gives roughly a 45° helix, which is
  // the range where real rope visually reads as rope (much tighter and it
  // stripes like candy, much looser and the strands look parallel).
  readonly uBraidPitch = uniform(4);

  private _shadowLight: THREE.DirectionalLight | null = null;

  constructor(length: number, phase: number, color?: THREE.Color) {
    super();
    this.uLength.value = length;
    this.uPhase.value = phase;
    if (color) this.uColor.value.set(color.r, color.g, color.b);

    this.positionNode = Fn(() => {
      // Geometry is centred: positionLocal.x ∈ [-L/2, +L/2]. Remap to
      // u ∈ [0, 1] so the end-mask clamps wobble to zero at both post
      // attachment points (u=0 and u=1).
      const u = positionLocal.x.div(max(this.uLength, 0.001)).add(0.5).toVar();
      const endMask = smoothstep(0, 0.08, u).mul(smoothstep(0, 0.08, float(1).sub(u)));

      const t = this.uTime.add(this.uPhase);
      const wave1 = sin(u.mul(2.7 * Math.PI).add(t.mul(1.9)));
      const wave2 = sin(u.mul(4.6 * Math.PI).add(t.mul(2.6))).mul(0.55);
      const dy = wave1.add(wave2).mul(this.uAmp).mul(endMask);
      const dz = sin(u.mul(3.1 * Math.PI).add(t.mul(1.3))).mul(this.uAmp).mul(0.7).mul(endMask);

      return positionLocal.add(vec3(0, dy, dz));
    })();

    this._buildColorNode();
  }

  setDirectionalLight(light: THREE.DirectionalLight): void {
    if (this._shadowLight === light) return;
    this._shadowLight = light;
    this._buildColorNode();
    this.needsUpdate = true;
  }

  private _buildColorNode(): void {
    const shadowLight = this._shadowLight;
    this.colorNode = Fn(() => {
      const shading = braidShading(uv(), this.uLength, this.uBraidPitch);
      // Strand centre = full brightness; seam = 40% brightness. The factor-
      // of-0.4 floor keeps the rope visibly coloured even in shadow between
      // strands instead of going to black.
      const albedoMul = shading.mul(0.6).add(0.4);

      // Cast-shadow attenuation, remapped to 0.5..1 so a shadowed rope
      // still reads as purple against the lava below rather than dropping
      // to a near-black silhouette.
      const visibility = (shadowLight
        ? (shadow(shadowLight) as unknown as FloatNode)
        : float(1)) as FloatNode;
      const shadowMul = mix(float(0.5), float(1.0), visibility);

      return vec4(this.uColor.mul(albedoMul).mul(shadowMul), 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}

// Same braided treatment for the corner posts — they're taller and thicker,
// so the twist pitch is slower (fewer rotations per metre) to match the
// proportions of a real rope-wrapped column. No positionNode: posts are
// static. `uLength` is the post height in metres; UVs on a CylinderGeometry
// run 0..1 top-to-bottom along Y so the shader formulation lines up without
// any geometry-side tricks.
export class PostMaterial extends NodeMaterial {
  readonly uColor      = uniform(new THREE.Vector3(0.290, 0.205, 0.565));
  readonly uLength     = uniform(1);
  readonly uBraidPitch = uniform(1);

  constructor(length: number, color?: THREE.Color) {
    super();
    this.uLength.value = length;
    if (color) this.uColor.value.set(color.r, color.g, color.b);

    this.colorNode = Fn(() => {
      const shading = braidShading(uv(), this.uLength, this.uBraidPitch);
      const albedoMul = shading.mul(0.55).add(0.45);
      return vec4(this.uColor.mul(albedoMul), 1);
    })();
  }
}
